import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import {
  Table,
  TableColumn,
  Button,
  PageHeader,
  Spinner,
  Input,
  Select,
  Badge,
  Avatar,
} from '../../components';
import type { SelectOption } from '../../components';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  Pencil1Icon,
  EnvelopeClosedIcon,
  CheckIcon,
  Cross2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@radix-ui/react-icons';
import {
  Employee,
  EMPLOYEE_STATUS_LABELS,
  EmployeeStatus,
} from '../../types/employee';
import { useEmployeeStore } from '../../store/employeeStore';
import { branchService } from '../../services/branchService';
import type { Branch } from '../../types/branch';
import {
  EmployeeFormPanel,
  EmployeeFormPanelMode,
} from './EmployeeFormPanel';
import { CreateBranchModal } from './CreateBranchModal';
import { useAuthStore } from '../../store/authStore';
import { hasFeaturePermission } from '../../utils/rbac';
import './EmployeesList.scss';

type PanelState =
  | { open: false }
  | {
      open: true;
      mode: EmployeeFormPanelMode;
      employeeId?: number;
      profilePictureUrl?: string | null;
    };

const DEBOUNCE_DELAY = 400;

const ROLE_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Roles' },
  { value: 'manager', label: 'Manager' },
  { value: 'relationship_manager', label: 'Relationship Executive' },
  { value: 'sales_executive', label: 'Sales Executive' },
];

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'deactivated', label: 'Deactivated' },
];

const ROLE_BADGE_VARIANT: Record<string, 'primary' | 'info' | 'success' | 'default'> = {
  Manager: 'info',
  'Relationship Executive': 'default',
  'Sales Executive': 'success',
};

export const EmployeesList: React.FC = () => {
  const role = useAuthStore(state => state.role);
  const canCreate = hasFeaturePermission(role, 'employee:create');
  const canEdit = hasFeaturePermission(role, 'employee:edit');
  const canDelete = hasFeaturePermission(role, 'employee:delete');

  const [panel, setPanel] = useState<PanelState>({ open: false });
  const [isCreateBranchOpen, setIsCreateBranchOpen] = useState(false);
  const [branchOptions, setBranchOptions] = useState<SelectOption[]>([
    { value: 'all', label: 'All Branches' },
  ]);

  const {
    employees,
    currentPage,
    pageSize,
    total,
    totalPages,
    searchQuery,
    roleFilter,
    branchFilter,
    statusFilter,
    isLoading,
    statusUpdatingEmployeeId,
    invitingEmployeeId,
    error,
    fetchEmployees,
    setPage,
    setRoleFilter,
    setBranchFilter,
    setStatusFilter,
    searchEmployees,
    clearFilters,
    toggleEmployeeStatus,
    sendInviteEmail,
  } = useEmployeeStore();

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const isFirstMount = useRef(true);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBranchFilterOptions = useCallback(async () => {
    try {
      const branches = await branchService.getBranches();
      setBranchOptions([
        { value: 'all', label: 'All Branches' },
        ...branches.map((branch) => ({
          value: String(branch.branchId),
          label: branch.name,
        })),
      ]);
    } catch {
      // Branch filter falls back to "All Branches" only
    }
  }, []);

  useEffect(() => {
    loadBranchFilterOptions();
  }, [loadBranchFilterOptions]);

  const handleBranchCreated = useCallback(
    (branch: Branch) => {
      setBranchOptions((prev) => {
        const exists = prev.some((option) => option.value === String(branch.branchId));
        if (exists) return prev;
        return [
          ...prev,
          { value: String(branch.branchId), label: branch.name },
        ].sort((a, b) => {
          if (a.value === 'all') return -1;
          if (b.value === 'all') return 1;
          return a.label.localeCompare(b.label);
        });
      });
    },
    []
  );

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchEmployees(localSearch);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearch, searchEmployees]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(localSearch.trim()) ||
    roleFilter !== 'all' ||
    branchFilter !== 'all' ||
    statusFilter !== 'all';

  const handleClearFilters = useCallback(() => {
    setLocalSearch('');
    clearFilters();
  }, [clearFilters]);

  const startEntry = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, total);

  const getFullName = useCallback((employee: Employee): string => {
    return (
      employee.fullName ||
      `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim()
    );
  }, []);

  const isEmployeeActive = useCallback(
    (status: EmployeeStatus) => status === 'active',
    []
  );

  const handleToggleEmployeeStatus = useCallback(
    async (employee: Employee) => {
      const isActive = isEmployeeActive(employee.status);
      const confirmed = window.confirm(
        isActive
          ? 'Are you sure you want to deactivate this employee?'
          : 'Activate this employee account?'
      );
      if (!confirmed) return;

      try {
        await toggleEmployeeStatus(employee.id, employee.status);
      } catch (err) {
        console.error('Failed to update employee status:', err);
      }
    },
    [toggleEmployeeStatus, isEmployeeActive]
  );

  const handleSendInvite = useCallback(
    async (employeeId: number) => {
      try {
        await sendInviteEmail(employeeId);
        window.alert('Invitation email sent successfully.');
      } catch (err) {
        console.error('Failed to send invitation email:', err);
      }
    },
    [sendInviteEmail]
  );

  const getPageNumbers = useCallback((): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [1];
    if (currentPage <= 4) {
      pages.push(2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const columns: TableColumn<Employee>[] = useMemo(
    () => [
      {
        key: 'employee',
        header: 'Employee',
        width: '32%',
        render: (employee) => {
          const name = getFullName(employee);
          return (
            <div className="employees-list__employee">
              <Avatar
                name={name}
                src={employee.profilePictureUrl ?? undefined}
                size="small"
              />
              <div className="employees-list__employee-info">
                <span className="employees-list__employee-name">{name}</span>
                <span className="employees-list__employee-email">
                  {employee.email}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'role',
        header: 'Role',
        width: '20%',
        render: (employee) => (
          <Badge
            variant={ROLE_BADGE_VARIANT[employee.role ?? ''] ?? 'default'}
            size="small"
            className="employees-list__role-badge"
          >
            {employee.role ?? '—'}
          </Badge>
        ),
      },
      {
        key: 'branch',
        header: 'Branch',
        width: '22%',
        render: (employee) => (
          <span className="employees-list__cell-text">
            {employee.department ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '16%',
        render: (employee) => (
          <span
            className={`employees-list__status employees-list__status--${employee.status}`}
          >
            <span className="employees-list__status-dot" aria-hidden="true" />
            {EMPLOYEE_STATUS_LABELS[employee.status]}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        align: 'right',
        width: '14%',
        render: (employee) => {
          const isUpdatingStatus = statusUpdatingEmployeeId === employee.id;
          const isSendingInvite = invitingEmployeeId === employee.id;
          const isActive = isEmployeeActive(employee.status);

          return (
            <div
              className="employees-list__actions"
              onClick={(e) => e.stopPropagation()}
            >
              {canEdit && (
                <button
                  type="button"
                  className="employees-list__action-btn"
                  title="Edit employee"
                  onClick={() =>
                    setPanel({
                      open: true,
                      mode: 'edit',
                      employeeId: employee.id,
                      profilePictureUrl: employee.profilePictureUrl,
                    })
                  }
                >
                  <Pencil1Icon />
                </button>
              )}
              {canEdit && employee.status === 'invited' && (
                <button
                  type="button"
                  className="employees-list__action-btn"
                  title="Resend invitation email"
                  disabled={isSendingInvite}
                  onClick={() => handleSendInvite(employee.id)}
                >
                  {isSendingInvite ? <Spinner size="small" /> : <EnvelopeClosedIcon />}
                </button>
              )}
              {canDelete && employee.status !== 'invited' && (
                <button
                  type="button"
                  className={`employees-list__action-btn ${isActive ? 'employees-list__action-btn--danger' : ''}`}
                  title={isActive ? 'Deactivate employee' : 'Activate employee'}
                  disabled={isUpdatingStatus}
                  onClick={() => handleToggleEmployeeStatus(employee)}
                >
                  {isUpdatingStatus ? (
                    <Spinner size="small" />
                  ) : isActive ? (
                    <Cross2Icon />
                  ) : (
                    <CheckIcon />
                  )}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [
      getFullName,
      isEmployeeActive,
      statusUpdatingEmployeeId,
      invitingEmployeeId,
      canEdit,
      canDelete,
      handleToggleEmployeeStatus,
      handleSendInvite,
    ]
  );

  const handleCreateEmployee = useCallback(() => {
    setPanel({ open: true, mode: 'create' });
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanel({ open: false });
  }, []);

  return (
    <div className="employees-list">
      <PageHeader
        title="Employee Management"
        subtitle="View, add and manage your organization's workforce."
        actions={
          canCreate ? (
            <div className="employees-list__header-actions">
              <Button
                variant="secondary"
                className="employees-list__create-branch-btn"
                onClick={() => setIsCreateBranchOpen(true)}
              >
                <PlusIcon />
                Create Branch
              </Button>
              <Button
                className="employees-list__add-btn"
                onClick={handleCreateEmployee}
              >
                <PlusIcon />
                Add New Employee
              </Button>
            </div>
          ) : undefined
        }
      />

      {error && (
        <div className="error-message">{error.message}</div>
      )}

      <div className="employees-list__toolbar">
        <div className="employees-list__toolbar-filters">
          <div className="employees-list__search">
            <MagnifyingGlassIcon
              className="employees-list__search-icon"
              aria-hidden="true"
            />
            <Input
              placeholder="Search by name, email or ID..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="employees-list__search-input"
            />
          </div>

          <div className="employees-list__filter-select">
            <Select
              value={roleFilter}
              onChange={setRoleFilter}
              options={ROLE_FILTER_OPTIONS}
              searchable={false}
              className="employees-list__select"
            />
          </div>

          <div className="employees-list__filter-select">
            <Select
              value={branchFilter}
              onChange={setBranchFilter}
              options={branchOptions}
              searchable={false}
              className="employees-list__select"
            />
          </div>

          <div className="employees-list__filter-select">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
              searchable={false}
              className="employees-list__select"
            />
          </div>
        </div>

        <button
          type="button"
          className="employees-list__clear-filters"
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear All
        </button>
      </div>

      <div className="employees-list__table-card">
        <Table
          className="employees-list__table"
          columns={columns}
          data={employees}
          isLoading={isLoading}
          emptyMessage={
            searchQuery
              ? `No employees found for "${searchQuery}"`
              : 'No employees found'
          }
        />

        {totalPages > 0 && (
          <div className="employees-list__pagination">
            <div className="employees-list__pagination-summary">
              Showing {startEntry} to {endEntry} of {total.toLocaleString()} employees
            </div>

            <div className="employees-list__pagination-controls">
              <button
                type="button"
                className="employees-list__page-btn"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeftIcon />
              </button>

              <div className="employees-list__page-numbers">
                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="employees-list__page-ellipsis"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={`page-${page}`}
                      type="button"
                      className={`employees-list__page-btn employees-list__page-btn--number ${page === currentPage ? 'employees-list__page-btn--active' : ''}`}
                      onClick={() => setPage(page as number)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                type="button"
                className="employees-list__page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
                aria-label="Next page"
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      <EmployeeFormPanel
        isOpen={panel.open}
        mode={panel.open ? panel.mode : 'create'}
        employeeId={panel.open ? panel.employeeId : undefined}
        initialProfilePictureUrl={
          panel.open ? panel.profilePictureUrl : undefined
        }
        onClose={handleClosePanel}
      />

      <CreateBranchModal
        isOpen={isCreateBranchOpen}
        onClose={() => setIsCreateBranchOpen(false)}
        onSuccess={handleBranchCreated}
      />
    </div>
  );
};
